import React, { useCallback, useEffect, useRef, useState } from 'react';
import { prop } from 'ramda';
import { useIsMounted } from 'js/hooks/useIsMounted';
import { useComponentTranslations } from 'js/hooks/component';
import { Autocomplete } from 'js/components/common/Fields/Autocomplete';
import { IAutocompleteOption } from 'js/components/common/Fields/Autocomplete/types';

import * as Types from '../../types';
import * as hooks from '../../hooks';
import * as helpers from '../../helpers';
import { SearchOption } from '../SearchOption';
import { useDynamicReducerSelector } from '../../../../store';
import { useFieldLabel } from '../../../ChangeFormField/hooks';

export const ZipCityLookup: React.FC<Types.IInnerLookupFieldConfig> = ({
    ChangeFormField,
    className,
    classNames,
    disabled,
    field,
    fieldId,
    isFieldHidden,
    onRetrieve,
    retrievedAddress,
    validate
}) => {
    const isMounted = useIsMounted();
    const translate = useComponentTranslations();
    const searchZipAndCity = hooks.useSearchZipAndCity();
    const hiddenFields = useDynamicReducerSelector(prop('hiddenFields'));
    const searchText = useRef<string>('');
    const [error, setError] = useState('');

    const zipField = field.innerFields.zip;
    const cityField = field.innerFields.city;
    const showZipInnerField = zipField && !hiddenFields.includes(zipField.name);
    const showCityInnerField = cityField && !hiddenFields.includes(cityField.name);
    const showAddressLookup = !(showZipInnerField || showCityInnerField);
    const [label, getLabelText] = useFieldLabel(field.label);
    const placeholder = field.placeholder && translate(field.placeholder, field.placeholder);

    const setAddressLookupError = useCallback((err: any) => {
        const defaultError = translate('playtech.mobile.system.form.addressLookupSwiss.serviceError.default');
        const message = translate(`playtech.mobile.system.form.addressLookupSwiss.serviceError.${err?.errorCode}`,
            err.errorMessage || defaultError
        );

        setError(message);
    }, [translate]);

    const onSearchChange = useCallback(async (value: string): Promise<IAutocompleteOption[] | void> => {
        setError('');
        searchText.current = value;

        try {
            const searchResults = await searchZipAndCity(value);

            return helpers.zipCitySearchResultsToOptions(searchResults, searchText.current);
        } catch (postCodeError: any) {
            isMounted() && setAddressLookupError(postCodeError);
        }
    }, [isMounted, searchZipAndCity, setAddressLookupError]);

    const onOptionSelect = useCallback(async (option: IAutocompleteOption): Promise<IAutocompleteOption[] | void> => {
        const selectedItem = option.data?.retrievedAddress as Types.IParsedAutoCompleteItem;

        setError('');
        searchText.current = '';

        onRetrieve(selectedItem);
    }, [onRetrieve]);

    useEffect(() => {
        if (!showAddressLookup) {
            setError('');
            searchText.current = '';
        }
    }, [showAddressLookup]);

    if (!zipField && !cityField) {
        return null;
    }

    const { isLabelAsteriskEnabled, isPlaceholderAsteriskEnabled } = field.validationRules?.find(rule => rule.type === 'required') || {};

    return (
        <>
            {showAddressLookup && (
                <Autocomplete
                    aria-label={getLabelText() || placeholder || translate('playtech.mobile.system.input.aria-label')}
                    className={className}
                    classNames={{ container: classNames?.autocomplete }}
                    disabled={disabled}
                    error={error}
                    id={fieldId}
                    infoTooltip={field.infoTooltip && translate(field.infoTooltip, field.infoTooltip)}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                    isPlaceholderAsteriskEnabled={isPlaceholderAsteriskEnabled}
                    label={label}
                    minSymbolsToSearch={2}
                    name={field.name}
                    noOptionsMessage={translate('playtech.mobile.system.form.addressLookupSwiss.serviceError.noResults')}
                    options={[]}
                    Option={SearchOption}
                    onSearchChange={onSearchChange}
                    onOptionSelect={onOptionSelect}
                    placeholder={placeholder}
                    validate={validate}
                />
            )}
            {showZipInnerField && (
                <ChangeFormField
                    field={zipField}
                    className={className}
                    isFieldHidden={isFieldHidden}
                    disabled={disabled || retrievedAddress?.zip}
                />
            )}
            {showCityInnerField && (
                <ChangeFormField
                    field={cityField}
                    className={className}
                    isFieldHidden={isFieldHidden}
                    disabled={disabled || retrievedAddress?.city}
                />
            )}
        </>
    );
};
